import { useCallback, useEffect } from "react";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import type {
  GetDevicesByLocationQuery,
  GetDevicesByLocationQueryVariables,
} from "../../../../../API";
import { GET_DEVICES_BY_LOCATION } from "../../../../../common/operations/queries";
import { errorNotification } from "../../../../../common/variables/notification";

export const DEVICES_TABLE_LIMIT = 100;

export const useGetDevicesByLocation = (locationId: string) => {
  const [getDevicesByLocation, { data, fetchMore, loading, networkStatus }] =
    useLazyQuery<GetDevicesByLocationQuery, GetDevicesByLocationQueryVariables>(
      GET_DEVICES_BY_LOCATION,
      {
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
      }
    );

  const fetchAllDevices = useCallback(
    async (currentLocationId: string) => {
      try {
        const response = await getDevicesByLocation({
          variables: {
            locationId: currentLocationId,
          },
        });

        let nextToken = response.data?.getDevicesByLocation?.nextToken;

        while (nextToken) {
          const moreResponse = await fetchMore({
            variables: {
              locationId: currentLocationId,
              nextToken: nextToken,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult?.getDevicesByLocation) {
                return prev;
              }

              const previousItems = prev.getDevicesByLocation?.items ?? [];
              const newItems = fetchMoreResult.getDevicesByLocation.items ?? [];

              return {
                ...prev,
                getDevicesByLocation: {
                  ...fetchMoreResult.getDevicesByLocation,
                  items: [...previousItems, ...newItems],
                },
              } as GetDevicesByLocationQuery;
            },
          });

          nextToken = moreResponse.data?.getDevicesByLocation?.nextToken;
        }
      } catch (e) {
        console.error(e);

        errorNotification(
          "Something went wrong while fetching connected devices."
        );
      }
    },
    [getDevicesByLocation, fetchMore]
  );

  useEffect(() => {
    if (locationId) {
      fetchAllDevices(locationId);
    }
  }, [locationId, fetchAllDevices]);

  const refetchDevices = (locationId: string) => {
    if (locationId) {
      fetchAllDevices(locationId);
    }
  };

  return {
    data,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    refetchDevices,
  };
};
